import { useState, useCallback } from "react";
import { useDialogState } from "../../../../hooks/useDialogState";
import Button from "@mui/material/Button";
import CircularProgress from "@mui/material/CircularProgress";
import IconButton from "@mui/material/IconButton";
import Snackbar from "@mui/material/Snackbar";
import Typography from "@mui/material/Typography";
import Alert from "@mui/material/Alert";
import AddIcon from "@mui/icons-material/Add";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import { SCORECARD, type ScorecardKey } from "@shared/scorecard";
import { rejectDocument } from "../../../../lib/corpusApi";
import { DocumentLink } from "../../../../components/domain/data-display/DocumentLink";
import { gaps } from "../../../../components/primitives/spacing";
import { LibraryPage } from "../LibraryPage";
import { LoadingState } from "../../../../components/primitives/feedback/LoadingState";
import {
  DataSequence,
  type DataSequenceColumn,
} from "../../../../components/primitives/data-display/DataSequence";
import { TierClearedBadges } from "../../../../components/domain/data-display/TierClearedBadges";
import { DocumentStatusBadge } from "../../../../components/domain/data-display/DocumentStatusBadge";
import { usePopupMenu } from "../../../../components/primitives/navigation/PopupMenu";
import { SearchBar } from "../../../../components/primitives/inputs/SearchBar";
import { DocumentFilters } from "./DocumentFilters";
import { useCorpusFeedOptions } from "./useCorpusFeedOptions";
import { useDocumentList } from "./useDocumentList";
import { useSubmitUrls } from "./useSubmitUrls";
import { useExtractionWarmup } from "../../useExtractionWarmup";
import SubmitUrlsDialog from "./SubmitUrlsDialog";
import { RejectDocumentDialog } from "./RejectDocumentDialog";
import { DocumentDetailDialog } from "./DocumentDetailDialog";
import type { QueueItem } from "../../../../lib/corpusApi";

const TIER1_FIELDS = (Object.keys(SCORECARD) as ScorecardKey[]).filter(
  (key) => SCORECARD[key].tier === 1
);

/** Lowest confidence across Tier-1 fields — the bottleneck that decides whether a document
 *  clears the auto-ingest gate. Null for rows predating Tangrowth (no confidence_scores). */
function minTier1Confidence(scores: Record<ScorecardKey, number> | null): number | null {
  if (!scores) return null;
  return Math.min(...TIER1_FIELDS.map((key) => scores[key] ?? 0));
}

function RowActions({
  doc,
  rowState,
  onDetails,
  onReject,
  onIngest,
  onAnnotate,
  onCopyId,
}: {
  doc: QueueItem;
  rowState: { loading: boolean; error: string | null; success: boolean } | undefined;
  onDetails: () => void;
  onReject: () => void;
  onIngest: () => void;
  onAnnotate: () => void;
  onCopyId: () => void;
}) {
  const { menu, setPopupMenuAnchor } = usePopupMenu([
    [
      { label: "Copy ID", onClick: onCopyId },
      { label: "Details", onClick: onDetails },
    ],
    ...(doc.status === "pending" ? [[{ label: "Annotate", onClick: onAnnotate }]] : []),
    ...(doc.status === "ready" ? [[{ label: "Ingest", onClick: onIngest }]] : []),
    ...(doc.status !== "ingested"
      ? [[{ label: "Reject", onClick: onReject, color: "error" as const }]]
      : []),
  ]);

  if (rowState?.loading) return <CircularProgress size={18} />;
  if (rowState?.error)
    return (
      <Typography variant="caption" color="error">
        {rowState.error}
      </Typography>
    );

  return (
    <>
      <IconButton size="small" onClick={setPopupMenuAnchor} aria-label="Row actions">
        <MoreVertIcon />
      </IconButton>
      {menu}
    </>
  );
}

/**
 * Documents: submit URLs + inspect the corpus in one place. Inspection only — annotation
 * happens in the backlog (Annotation), not from here.
 */
export default function Documents() {
  const [submitOpen, setSubmitOpen] = useState(false);
  const [copiedOpen, setCopiedOpen] = useState(false);
  const [rejectTarget, setRejectTarget] = useState<string | null>(null);
  const detailDialog = useDialogState<QueueItem>();
  const {
    documents,
    loading,
    error,
    filters,
    setFilters,
    query,
    setQuery,
    rowState,
    handleIngest,
    handleAnnotate,
    pagination,
    refresh,
  } = useDocumentList();

  const feeds = useCorpusFeedOptions();

  const handleQueryChange = useCallback((v: string) => setQuery(v), [setQuery]);
  const submission = useSubmitUrls();
  const { warm: extractionWarm, sourceConnectors } = useExtractionWarmup();

  function handleCopyId(id: string) {
    navigator.clipboard.writeText(id).catch(() => {});
    setCopiedOpen(true);
  }

  const columns: DataSequenceColumn<QueueItem>[] = [
    {
      key: "title",
      header: "Title",
      width: 280,
      cardRole: "primary",
      cell: (doc) => <DocumentLink title={doc.title ?? doc.url} url={doc.url} />,
    },
    {
      key: "status",
      header: "Status",
      cell: (doc) => <DocumentStatusBadge status={doc.status} />,
    },
    {
      key: "rank_stability",
      header: "Rank stability",
      cell: (doc) =>
        doc.rank_stability !== null ? `${Math.round(doc.rank_stability * 100)}%` : "—",
    },
    {
      key: "tier_cleared",
      header: "Tier cleared",
      cell: (doc) => (
        <TierClearedBadges status={doc.tier_cleared} confidenceScores={doc.confidence_scores} />
      ),
    },
    {
      key: "confidence",
      header: "Confidence",
      cell: (doc) => {
        const min = minTier1Confidence(doc.confidence_scores);
        return min !== null ? `${Math.round(min * 100)}%` : "—";
      },
    },
    {
      key: "submitted_at",
      header: "Submitted",
      cell: (doc) => (
        <Typography variant="caption" color="text.secondary">
          {new Date(doc.submitted_at).toLocaleDateString()}
        </Typography>
      ),
    },
    {
      key: "actions",
      header: "",
      cardRole: "action",
      cell: (doc) => (
        <RowActions
          doc={doc}
          rowState={rowState[doc.id]}
          onDetails={() => detailDialog.open(doc)}
          onReject={() => setRejectTarget(doc.id)}
          onIngest={() => handleIngest(doc.id)}
          onAnnotate={() => handleAnnotate(doc.id)}
          onCopyId={() => handleCopyId(doc.id)}
        />
      ),
    },
  ];

  return (
    <LibraryPage
      actions={
        <Button variant="contained" startIcon={<AddIcon />} onClick={() => setSubmitOpen(true)}>
          Submit URLs
        </Button>
      }
    >
      {error && <Alert severity="error">{error}</Alert>}

      <DocumentFilters filters={filters} onChange={setFilters} feeds={feeds} />
      <SearchBar
        value={query}
        onChange={handleQueryChange}
        placeholder="Search title or URL…"
        ariaLabel="Search documents"
      />
      {loading ? (
        <LoadingState />
      ) : (
        <DataSequence
          columns={columns}
          rows={documents}
          getRowId={(doc) => doc.id}
          emptyMessage="No documents match."
          pagination={pagination}
        />
      )}

      <SubmitUrlsDialog
        open={submitOpen}
        onClose={() => setSubmitOpen(false)}
        submission={submission}
        onSubmitted={refresh}
        extractionWarm={extractionWarm}
        sourceConnectors={sourceConnectors}
      />

      <Snackbar
        open={copiedOpen}
        onClose={() => setCopiedOpen(false)}
        autoHideDuration={2500}
        message="Document ID copied"
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      />

      <RejectDocumentDialog
        open={rejectTarget !== null}
        onClose={() => setRejectTarget(null)}
        onConfirm={async (code, message) => {
          await rejectDocument(rejectTarget!, code, message);
          refresh();
        }}
      />

      <DocumentDetailDialog item={detailDialog.value} onClose={detailDialog.close} />
    </LibraryPage>
  );
}
